import type {
    ButtonProps,
    LoginModalProps,
    ShorthandValue,
    ComponentEventHandler,
    DialogProps,
} from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    useDeviceBrowserContext,
} from '@archipro-design/aria';
import { Flex } from '@archipro-design/aria';
import { Subheader } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import { useTheme } from '@archipro-design/aria';
import { Caption } from '@archipro-design/aria';
import { LoginModal } from '@archipro-design/aria';
import { Divider } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import { ArrowLeftLine } from '@archipro-design/icons';
import { useLatest } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useEnquirySession } from '~/modules/enquiry/hooks/use-enquiry';
import { usePublicUser } from '../../hook/use-public-user';
import ForgotPasswordForm from '../forgot-password/ForgotPasswordForm';
import type { LoginSignUpFormProps } from '../login-signup-form/LoginSignUpForm';
import { useBoolean } from 'ahooks';
import LoginSignUpForm from '../login-signup-form/LoginSignUpForm';
import SocialLogin from '../social-login/SocialLogin';
import * as S from './LoginSignUpModal.style';
import type { LoginFlow } from '../../hook/use-login-flow';
import { PolicyLink } from '../policy-link/PolicyLink';
import { useBypassForcedSocial } from '../../hook/use-bypass-forced-social';
import { useRootData } from '~/modules/root';
import cx from 'classnames';
import { useTracker } from '@archipro-website/tracker';
import { useSocialLogins } from '~/modules/user/component/social-login/hook/use-social-logins';
import { useFeature } from '~/modules/root/hook/use-feature';
import { AuthModal } from '~/modules-rocco/user/component/auth-modal/AuthModal';

interface LoginSignUpModalProps {
    login: LoginModalProps &
        Pick<
            LoginSignUpFormProps,
            | 'flow'
            | 'defaultPublicUser'
            | 'redirect'
            | 'allowExternalUrls'
            | 'message'
            | 'skipPostSignUp'
            | 'authSource'
        > & {
            subtitle?: string;
            disableCloseButton?: boolean;
            /** Custom left-pane content for the new auth modal (replaces the default brand pane). */
            brandPane?: React.ReactNode;
            onSuccess?: ComponentEventHandler<DialogProps>;
            onAuthSuccess: () => void;
        };
    forgotPassword: LoginModalProps;
}

const LegacyLoginSignUpModal = (props: LoginSignUpModalProps) => {
    const theme = useTheme();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const { login, forgotPassword } = props;
    const {
        flow,
        defaultPublicUser,
        authSource,
        allowExternalUrls,
        skipPostSignUp,
        redirect,
        onAuthSuccess,
        onSuccess: _,
        header,
        ...loginModalProps
    } = login;
    const { css } = useStyles();
    const [form, setForm] = useState<'forgot-password' | 'login'>('login');
    const [emailCodeStepActive, setEmailCodeStepActive] = useState(false);
    const { enquirySession } = useEnquirySession();
    const { publicUser, handleUserExists } = usePublicUser(
        login.authSource,
        defaultPublicUser,
    );

    const { env } = useRootData();

    const tracker = useTracker();

    const { bypassForcedSocial } = useBypassForcedSocial(env.stage);
    const [useEmail, setUseEmail] = useState(false);

    const { isInstagramOrFacebookApp } = useDeviceBrowserContext();
    const availableSocialLogins = useSocialLogins({
        loginOptions: publicUser?.socialLoginOptions,
        authSource,
        redirect,
        skipPostSignUp: !!skipPostSignUp,
        isInstagramOrFacebookApp,
    });

    const hideEmailAndButton =
        publicUser?.socialLoginOptions.length === 1 &&
        !useEmail &&
        !bypassForcedSocial;

    const getFlow = (): LoginFlow => {
        const isPostEnquiry = flow?.includes('post-enquiry');
        if (publicUser?.type === 'new') {
            return isPostEnquiry ? 'post-enquiry-sign-up' : 'sign-up';
        }
        if (publicUser?.type === 'existing') {
            return isPostEnquiry ? 'post-enquiry-login' : 'login';
        } else return null;
    };

    useEffect(() => {
        if (!loginModalProps.open) return;
        tracker.log('AuthModalImpression', {
            url: new URL(window.location.href),
            data: { Source: authSource },
            ga4Data: { source: authSource },
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });
        if (availableSocialLogins.length === 0) {
            setUseEmail(true);
        }
    }, [
        authSource,
        loginModalProps.open,
        tracker,
        availableSocialLogins.length,
    ]);

    const { current: loginFlow } = useLatest(getFlow());

    const renderHeader = () => {
        if (loginFlow === 'login' || loginFlow === 'post-enquiry-login')
            return 'Log in';
        if (loginFlow === 'sign-up' || loginFlow === 'post-enquiry-sign-up')
            return 'Sign up';
        return 'Log in or sign up';
    };

    const renderWelcomeMessage = useMemo(() => {
        if (defaultPublicUser?.type === 'new' && enquirySession?.name) {
            return `Welcome, ${enquirySession.name}`;
        }
        if (publicUser?.type === 'existing' && publicUser?.firstName) {
            return `Welcome back, ${publicUser.firstName}`;
        } else if (typeof header === 'string' && header) {
            return header;
        } else {
            return 'Welcome to ArchiPro';
        }
    }, [defaultPublicUser?.type, enquirySession, publicUser, header]);

    const hasWelcomeBackMessage =
        renderWelcomeMessage?.includes('Welcome back,');

    const renderFooterContent = () => {
        if (
            !loginFlow ||
            loginFlow === 'login' ||
            loginFlow === 'post-enquiry-login'
        ) {
            return (
                <Caption
                    link
                    variant="05-alt"
                    onClick={() => {
                        setForm('forgot-password');
                    }}
                    variables={{
                        captionColor: theme.siteVariables.colors.charcoal[250],
                    }}
                >
                    Forgot password?
                </Caption>
            );
        }

        return (
            <div className={css(S.FormLink)}>
                <Caption variant="05-alt" inline>
                    By signing up, you have read and agree to our
                </Caption>
                <PolicyLink
                    variant="05-alt"
                    inline={true}
                    link={true}
                    target="_blank"
                    as="a"
                    design={{ paddingLeft: pxToRem(3) }}
                    style={{ textDecoration: 'underline' }}
                />
            </div>
        );
    };

    const [forgotPasswordCompleted, setForgotPasswordCompleted] = useBoolean();
    const forgotPasswordHeader = forgotPasswordCompleted
        ? 'Reset password link sent'
        : `Forgot password?`;

    const handleForgotPasswordBack = () => {
        if (forgotPasswordCompleted) {
            return setForgotPasswordCompleted.setFalse();
        } else {
            setForm('login');
        }
    };

    const hideFooter =
        loginFlow === 'post-enquiry-login' &&
        !!publicUser?.socialLoginOptions?.length;

    const linkButtonStyle = {
        textDecoration: 'underline',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        padding: 0,
        margin: 0,
        color: 'inherit',
    };

    // TODO: Remove this when this is replaced with the Rocco Dialog component.
    useEffect(() => {
        if (
            typeof document !== 'undefined' &&
            (loginModalProps.open || forgotPassword.open)
        ) {
            document.body.setAttribute('data-scroll-locked', '1');

            return () => {
                document.body.removeAttribute('data-scroll-locked');
            };
        }
        return undefined;
    }, [loginModalProps.open, forgotPassword.open]);

    useEffect(() => {
        if (!loginModalProps.open) {
            setEmailCodeStepActive(false);
        }
    }, [loginModalProps.open]);

    const handleLoginModalClose: ComponentEventHandler<DialogProps> = (e) => {
        login?.onCancel?.(e);
        e.stopPropagation();
    };

    return (
        <>
            {form === 'login' && (
                <LoginModal
                    className={css(
                        authSource === 'siteGate'
                            ? S.LoginModalSiteGate
                            : S.LoginModal,
                    )}
                    header={renderHeader()}
                    overlay={{
                        className: cx(
                            isMobile && css(S.OverlayClass),
                            authSource === 'siteGate' && css(S.SiteGateOverlay),
                        ),
                    }}
                    headerAction={
                        login.disableCloseButton
                            ? null
                            : ({
                                  onClick: handleLoginModalClose,
                              } satisfies ShorthandValue<ButtonProps>)
                    }
                    content={
                        <Flex column className={css(S.LoginModalContent)}>
                            <Subheader variant="02">
                                {renderWelcomeMessage}
                            </Subheader>
                            {login.subtitle && (
                                <Caption
                                    variant="03-alt"
                                    className={css(S.LoginSubtitle)}
                                    variables={{
                                        captionColor:
                                            theme.siteVariables.colors
                                                .charcoal[100],
                                        fontSize: pxToRem(14),
                                        lineHeight: pxToRem(17.5),
                                    }}
                                >
                                    {login.subtitle}
                                </Caption>
                            )}

                            {!hasWelcomeBackMessage && !login.subtitle && (
                                <Caption
                                    variant="03-alt"
                                    className={css(S.LoginSubtitle)}
                                    variables={{
                                        captionColor:
                                            theme.siteVariables.colors
                                                .charcoal[100],
                                        fontSize: pxToRem(14),
                                        lineHeight: pxToRem(17.5),
                                    }}
                                >
                                    Log in or sign up for free to continue
                                </Caption>
                            )}

                            {login.message && (
                                <Caption
                                    variant="03-alt"
                                    className={cx(
                                        css(S.LoginMessage),
                                        css(S.CommonCaption),
                                    )}
                                >
                                    {login.message}
                                </Caption>
                            )}

                            {loginFlow === 'post-enquiry-sign-up' && (
                                <Caption
                                    variant="03-alt"
                                    className={css(S.CommonCaption)}
                                >
                                    Great, you've made your first enquiry.&nbsp;
                                    <br />
                                    Connect your social media or enter a
                                    password to complete your account sign up.
                                </Caption>
                            )}

                            <SocialLogin
                                loginOptions={publicUser?.socialLoginOptions}
                                preOptionText={
                                    hideEmailAndButton
                                        ? 'Continue with'
                                        : undefined
                                }
                                useEmail={useEmail}
                                authSource={authSource}
                                skipPostSignUp={!!login.skipPostSignUp}
                                redirect={login.redirect}
                            />

                            {hideEmailAndButton ? (
                                <div style={{ width: '100%' }}>
                                    <Caption
                                        variant="04"
                                        variables={{
                                            color: theme.siteVariables.colors
                                                .charcoal[100],
                                        }}
                                        design={{ marginTop: pxToRem(12) }}
                                        className={css(S.CommonCaption)}
                                    >
                                        <div>
                                            Not you? &nbsp;
                                            <button
                                                onClick={() => {
                                                    setUseEmail(true);
                                                }}
                                                style={linkButtonStyle}
                                            >
                                                Use another account
                                            </button>
                                        </div>
                                        <div style={{ marginTop: pxToRem(8) }}>
                                            Or, &nbsp;
                                            <button
                                                onClick={() => {
                                                    setUseEmail(true);
                                                }}
                                                style={linkButtonStyle}
                                            >
                                                log in with password
                                            </button>
                                        </div>
                                    </Caption>
                                </div>
                            ) : (
                                <>
                                    {availableSocialLogins.length > 0 && (
                                        <Divider
                                            color="grey"
                                            content={'or'}
                                            size={1}
                                            design={{
                                                width: '100%',
                                                marginTop: pxToRem(14),
                                            }}
                                            variables={{
                                                dividerPrimaryColor:
                                                    theme.siteVariables.colors
                                                        .charcoal[100],
                                                dividerPadding: pxToRem(24),
                                                textFontSize: pxToRem(12),
                                            }}
                                        />
                                    )}
                                    <LoginSignUpForm
                                        onEmailBlur={handleUserExists}
                                        onResetPassword={(e) => {
                                            setForm('forgot-password');
                                            e.preventDefault(); // Prevent following <a href> to legacy password reset
                                        }}
                                        emailCodeStepActive={
                                            emailCodeStepActive
                                        }
                                        onEmailCodeStepActiveChange={
                                            setEmailCodeStepActive
                                        }
                                        onEmailCodeStepChange={
                                            setEmailCodeStepActive
                                        }
                                        defaultPublicUser={publicUser}
                                        {...login}
                                        flow={loginFlow}
                                        allowExternalUrls={allowExternalUrls}
                                        skipPostSignUp={!!skipPostSignUp}
                                        redirect={redirect}
                                        onSuccess={onAuthSuccess}
                                    />
                                </>
                            )}

                            {!hideFooter && !emailCodeStepActive && (
                                <div
                                    style={{
                                        textAlign: 'center',
                                        marginTop: pxToRem(14),
                                    }}
                                >
                                    {renderFooterContent()}
                                </div>
                            )}
                        </Flex>
                    }
                    footerButton={null}
                    footerContent={null}
                    {...loginModalProps}
                    variables={
                        !isMobile
                            ? {
                                  rootBorderRadius: pxToRem(16),
                              }
                            : undefined
                    }
                />
            )}
            {form === 'forgot-password' && (
                <LoginModal
                    className={css(S.ForgotPasswordModal)}
                    header={forgotPasswordHeader}
                    headerAction={{
                        icon: <ArrowLeftLine />,
                        size: 20,
                        onClick: handleForgotPasswordBack,
                    }}
                    content={
                        <ForgotPasswordForm
                            flow={
                                forgotPasswordCompleted
                                    ? 'forgot-password-completed'
                                    : 'forgot-password'
                            }
                            onBack={() => {
                                setForm('login');
                                setForgotPasswordCompleted.setFalse();
                            }}
                            onSuccess={() => {
                                setForgotPasswordCompleted.setTrue();
                            }}
                        />
                    }
                    footerButton={null}
                    footerContent={null}
                    variables={
                        !isMobile
                            ? {
                                  rootBorderRadius: pxToRem(16),
                              }
                            : undefined
                    }
                    {...forgotPassword}
                />
            )}
        </>
    );
};

/**
 * The site-wide login/signup modal. Use useAuth (or the jotai login-modal
 * atoms) to show or hide it — don't render it directly.
 *
 * Renders the revamped two-pane AuthModal when the auth_modal_2026 feature
 * flag is on, the legacy aria modal otherwise; every render site keeps this
 * component's props contract either way.
 */
const LoginSignUpModal = (props: LoginSignUpModalProps) => {
    const newAuthModalEnabled = useFeature('auth_modal_2026');
    const { login } = props;

    if (newAuthModalEnabled) {
        return (
            <AuthModal
                open={!!login.open}
                authSource={login.authSource}
                flow={login.flow}
                redirect={login.redirect}
                allowExternalUrls={login.allowExternalUrls}
                skipPostSignUp={login.skipPostSignUp}
                disableCloseButton={login.disableCloseButton}
                header={
                    typeof login.header === 'string' ? login.header : undefined
                }
                subtitle={login.subtitle}
                message={login.message}
                defaultPublicUser={login.defaultPublicUser}
                brandPane={login.brandPane}
                onCancel={() => {
                    (login.onCancel as unknown as (() => void) | undefined)?.();
                }}
                onAuthSuccess={login.onAuthSuccess}
            />
        );
    }

    return <LegacyLoginSignUpModal {...props} />;
};

export default LoginSignUpModal;
